import { Box, Typography, useTheme } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

export type BrandBadgeSize = "small" | "medium";

interface BrandBadgeProps {
  /** バッジに表示するラベル。 */
  label: string;
  /** header 用は "small"、ページ内は "medium"（既定）。 */
  size?: BrandBadgeSize;
}

/**
 * 月額会員バッジ（汎用）。
 *
 * 「ダーク基調＋ゴールドの差し色」の上質なプレミアムピル。
 * 深いチャコール／ネイビー系のソリッド地に、細いゴールドの枠線・小さなゴールドの王冠アイコン・
 * シャンパンゴールドの文字（weight600）を効かせ、ダーク×ゴールドのコントラストで高級感を出す。
 * 派手なグラデ・強い白グロー・極太は用いず上品に。ラベルのみ label prop で差し替え可能。
 * ライト/ダーク両対応（ダークテーマ時も破綻しないよう明度調整）。
 *
 * 表示可否（会員判定・フラグ判定）は呼び出し側で行う（useIsMonthlyMember 等）。
 */
export const BrandBadge: React.FC<BrandBadgeProps> = ({
  label,
  size = "medium",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const compact = size === "small";

  // ダーク地（ライトテーマ上ではやや明るめのチャコール、ダークテーマではさらに明度を上げて浮かせる）。
  const background = isDark
    ? "linear-gradient(180deg, #2b3644 0%, #26313f 100%)"
    : "linear-gradient(180deg, #26313f 0%, #1f2a37 100%)";
  // ゴールドの差し色（枠線・アイコン・文字）。
  const borderColor = isDark
    ? "rgba(231, 210, 160, 0.6)"
    : "rgba(212, 178, 106, 0.55)";
  const goldIcon = "#e7d2a0"; // シャンパンゴールド
  const goldText = "#e7d2a0";

  return (
    <Box
      component="span"
      role="img"
      aria-label={label}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0.4 : 0.55,
        pl: compact ? 0.7 : 0.95,
        pr: compact ? 0.95 : 1.2,
        py: compact ? 0.22 : 0.38,
        borderRadius: 999,
        background,
        border: `1px solid ${borderColor}`,
        color: goldText,
        whiteSpace: "nowrap",
        // ダーク地の控えめな立体感のみ（強い影・グローは避ける）。
        boxShadow: "0 1px 2px rgba(0,0,0,0.28)",
      }}
    >
      <WorkspacePremiumIcon
        sx={{ fontSize: compact ? 14 : 16, color: goldIcon }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: compact ? 11 : 12,
          fontWeight: 600,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};
